import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../../../core/services/api.service';
import { finalize } from 'rxjs/operators';
import { TranslatePipe } from '../../../../../../core/pipes/translate.pipe';
import { SeasonalPricingPanel } from '../../../../owner/Components/seasonal-pricing-panel/seasonal-pricing-panel';
import { AvailabilityComponent } from '../../../../owner/Components/availability/availability';
import { AuthService } from '../../../../../../core/auth/services/auth.service';
import { HasPermissionDirective } from '../../../../../../core/auth/directives/has-permission.directive';
import { AddNewProperty } from '../../../../add-new-property/add-new-property';
import { UpdateProperty } from '../../../../update-property/update-property';
import { RouterModule } from '@angular/router';

// ── Interfaces ────────────────────────────────────────────────────────────────

interface UnitLocation {
  country: { en: string; ar: string };
  city: { en: string; ar: string };
  area: { en: string; ar: string };
  address: { en: string; ar: string };
  coordinates: {
    type: string;
    coordinates: [number, number];
  };
}

interface GalleryItem {
  secure_url: string;
  public_id: string;
  _id: string;
}

interface DocumentItem {
  secure_url: string;
  public_id: string;
  _id: string;
}

interface PriceTier {
  minDays: number;
  pricePerDay: number;
  label: string;
}

interface RentPrice {
  rentType: 'daily' | 'weekly' | 'monthly';
  price: number;
  currency: string;
  commission: {
    type: 'fixed' | 'percentage';
    value: number;
  };
  securityDeposit: number;
  tiers: PriceTier[];
}

interface SalePlan {
  name_en: string;
  name_ar: string;
  saleType: 'cash' | 'installment';
  downPaymentPercent?: number;
  downPaymentAmount?: number;
  years?: number;
  interestRate?: number;
  discountPercent?: number;
}

interface SalePrice {
  basePrice: number;
  pricePerMeter: number;
  currency: string;
  negotiable: boolean;
  commission: {
    type: 'fixed' | 'percentage';
    value: number;
  };
  plans: SalePlan[];
}

export interface Unit {
  _id: string;
  title_en: string;
  title_ar: string;
  title: string;
  description_en: string;
  description_ar: string;
  unitNumber: string;
  unitType_en: string;
  unitType_ar: string;
  unitType: string;
  buildYear: number;
  utilities_en: string[];
  utilities_ar: string[];
  purpose: 'rent' | 'sale' | 'sale_and_rent';
  status: 'active' | 'sold' | 'rented' | 'inactive';
  unitArea: number;
  bedrooms: number;
  bathrooms: number;
  floorNumber: number;
  furnished: boolean;
  maxGuests: number;
  isFeatured: boolean;
  isTrusted: boolean;
  viewsCount: number;
  averageRating: number;
  totalReviews: number;
  location: UnitLocation;
  gallery: GalleryItem[];
  documents: DocumentItem[];
  facilitiesAndServices_en: string[];
  facilitiesAndServices_ar: string[];
  facilitiesAndServices: string[];
  rentPrice?: RentPrice;
  salePrice?: SalePrice;
  projectId?: string;
  propertyId?: string;
  createdAt: string;
  updatedAt: string;
}

interface UnitsResponse {
  data: Unit[];
  pages: number;
  currentPage: number;
  totalItems: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-units',
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    SeasonalPricingPanel,
    AvailabilityComponent,
    HasPermissionDirective,
    AddNewProperty,
    UpdateProperty,
    RouterModule
  ],
  templateUrl: './units.html',
})
export class BrokerUnits implements OnInit {
  units: Unit[] = [];
  isLoading = false;
  error = '';
  totalItems = 0;
  currentPage = 1;
  totalPages = 1;

  // Array reference for template
  Array = Array;

  // Filters
  filters = {
    projectId: '',
    title: '',
    unitType: '',
    purpose: '',
    furnished: '',
    rentPeriod: '',
    status: '',
    page: 1,
    limit: 12,
  };

  // Modals
  showDeleteModal = false;
  showPriceModal = false;
  showGalleryModal = false;
  showDocumentsModal = false;
  showFeaturesModal = false;
  showCreateModal = false;
  showEditModal = false;

  // Panels
  pricingUnitId: string | null = null;
  availabilityUnitId: string | null = null;

  // Selected unit for operations
  selectedUnit: Unit | null = null;
  editingUnit: Unit | null = null;

  // Loading states
  deleteLoading = false;
  galleryLoading = false;
  documentsLoading = false;
  featuresLoading = false;
  priceLoading = false;

  // File uploads
  selectedGalleryFiles: File[] = [];
  selectedDocumentFiles: File[] = [];
  galleryToDelete: string[] = [];
  documentsToDelete: string[] = [];

  // Features management
  newFeatureEn = '';
  newFeatureAr = '';

  // Price form data
  priceForm = {
    rentPrice: {
      rentType: 'daily' as 'daily' | 'weekly' | 'monthly',
      price: 0,
      currency: 'EGP',
      commission: {
        type: 'percentage' as 'fixed' | 'percentage',
        value: 0,
      },
      securityDeposit: 0,
      tiers: [] as PriceTier[],
    },
    salePrice: {
      basePrice: 0,
      pricePerMeter: 0,
      currency: 'EGP',
      negotiable: false,
      commission: {
        type: 'percentage' as 'fixed' | 'percentage',
        value: 0,
      },
      plans: [] as SalePlan[],
    },
  };

  constructor(
    private api: ApiService,
    public auth: AuthService,
  ) { }

  ngOnInit(): void {
    this.load();
  }

  // ── Data Loading ─────────────────────────────────────────────────────────────

  load(): void {
    this.isLoading = true;
    this.error = '';

    const params = new URLSearchParams();
    Object.entries(this.filters).forEach(([key, value]) => {
      if (value) params.append(key, value.toString());
    });

    this.api
      .get<UnitsResponse>(`/broker/unit/dashboard?${params.toString()}`)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res) => {
          this.units = res.data ?? [];
          this.totalItems = res.totalItems ?? 0;
          this.currentPage = res.currentPage ?? 1;
          this.totalPages = res.pages ?? 1;
        },
        error: (err) => (this.error = err?.message || 'Failed to load units.'),
      });
  }

  applyFilters(): void {
    this.filters.page = 1;
    this.load();
  }

  resetFilters(): void {
    this.filters = {
      projectId: '',
      title: '',
      unitType: '',
      purpose: '',
      furnished: '',
      rentPeriod: '',
      status: '',
      page: 1,
      limit: 12,
    };
    this.load();
  }

  changePage(page: number): void {
    this.filters.page = page;
    this.load();
  }

  // ── Create Unit ──────────────────────────────────────────────────────────────

  openCreateModal(): void {
    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
  }

  onUnitCreated(): void {
    this.closeCreateModal();
    this.load();
  }

  // ── Edit Unit ────────────────────────────────────────────────────────────────

  openEditModal(unit: Unit): void {
    this.editingUnit = unit;
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.editingUnit = null;
    this.load();
  }

  // ── Delete Unit ──────────────────────────────────────────────────────────────

  openDeleteModal(unit: Unit): void {
    this.selectedUnit = unit;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedUnit = null;
  }

  deleteUnit(): void {
    if (!this.selectedUnit) return;

    this.deleteLoading = true;

    this.api
      .delete<{ message: string }>(`/broker/unit/${this.selectedUnit._id}`)
      .pipe(finalize(() => (this.deleteLoading = false)))
      .subscribe({
        next: (res) => {
          this.closeDeleteModal();
          this.load();
          alert('Unit deleted successfully!');
        },
        error: (err) => alert(err?.message || 'Failed to delete unit.'),
      });
  }

  // ── Gallery Management ───────────────────────────────────────────────────────

  openGalleryModal(unit: Unit): void {
    this.selectedUnit = unit;
    this.selectedGalleryFiles = [];
    this.galleryToDelete = [];
    this.showGalleryModal = true;
  }

  closeGalleryModal(): void {
    this.showGalleryModal = false;
    this.selectedUnit = null;
    this.selectedGalleryFiles = [];
    this.galleryToDelete = [];
  }

  onGalleryFilesSelected(event: any): void {
    const files = Array.from(event.target.files) as File[];
    this.selectedGalleryFiles = files;
  }

  toggleGalleryDelete(publicId: string): void {
    const index = this.galleryToDelete.indexOf(publicId);
    if (index > -1) {
      this.galleryToDelete.splice(index, 1);
    } else {
      this.galleryToDelete.push(publicId);
    }
  }

  updateGallery(): void {
    if (!this.selectedUnit) return;

    this.galleryLoading = true;

    const formData = new FormData();

    // Add files to delete
    this.galleryToDelete.forEach((id) => {
      formData.append('deleteGalleryIds', id);
    });

    // Add new files
    this.selectedGalleryFiles.forEach((file) => {
      formData.append('gallery', file);
    });

    this.api
      .patch<{ unit: Unit }>(`/broker/unit/${this.selectedUnit._id}/gallery`, formData)
      .pipe(finalize(() => (this.galleryLoading = false)))
      .subscribe({
        next: (res) => {
          this.closeGalleryModal();
          this.load();
          alert('Gallery updated successfully!');
        },
        error: (err) => alert(err?.message || 'Failed to update gallery.'),
      });
  }

  // ── Documents Management ─────────────────────────────────────────────────────

  openDocumentsModal(unit: Unit): void {
    this.selectedUnit = unit;
    this.selectedDocumentFiles = [];
    this.documentsToDelete = [];
    this.showDocumentsModal = true;
  }

  closeDocumentsModal(): void {
    this.showDocumentsModal = false;
    this.selectedUnit = null;
    this.selectedDocumentFiles = [];
    this.documentsToDelete = [];
  }

  onDocumentFilesSelected(event: any): void {
    const files = Array.from(event.target.files) as File[];
    this.selectedDocumentFiles = files;
  }

  toggleDocumentDelete(publicId: string): void {
    const index = this.documentsToDelete.indexOf(publicId);
    if (index > -1) {
      this.documentsToDelete.splice(index, 1);
    } else {
      this.documentsToDelete.push(publicId);
    }
  }

  updateDocuments(): void {
    if (!this.selectedUnit) return;

    this.documentsLoading = true;

    const formData = new FormData();

    // Add files to delete
    this.documentsToDelete.forEach((id) => {
      formData.append('deleteDocumentsIds', id);
    });

    // Add new files
    this.selectedDocumentFiles.forEach((file) => {
      formData.append('documents', file);
    });

    this.api
      .patch<{ unit: Unit }>(`/broker/unit/${this.selectedUnit._id}/documents`, formData)
      .pipe(finalize(() => (this.documentsLoading = false)))
      .subscribe({
        next: (res) => {
          this.closeDocumentsModal();
          this.load();
          alert('Documents updated successfully!');
        },
        error: (err) => alert(err?.message || 'Failed to update documents.'),
      });
  }

  // ── Features Management ──────────────────────────────────────────────────────

  openFeaturesModal(unit: Unit): void {
    this.selectedUnit = unit;
    this.showFeaturesModal = true;
  }

  closeFeaturesModal(): void {
    this.showFeaturesModal = false;
    this.selectedUnit = null;
    this.newFeatureEn = '';
    this.newFeatureAr = '';
  }

  addFeature(): void {
    if (!this.selectedUnit || !this.newFeatureEn.trim() || !this.newFeatureAr.trim()) return;

    const updateData = {
      facilitiesAndServices_en: [
        ...this.selectedUnit.facilitiesAndServices_en,
        this.newFeatureEn.trim(),
      ],
      facilitiesAndServices_ar: [
        ...this.selectedUnit.facilitiesAndServices_ar,
        this.newFeatureAr.trim(),
      ],
    };

    this.updateFeatures(updateData);
  }

  removeFeature(index: number): void {
    if (!this.selectedUnit) return;

    const updateData = {
      deleteInFacilitiesAndServices_en: [this.selectedUnit.facilitiesAndServices_en[index]],
      deleteInFacilitiesAndServices_ar: [this.selectedUnit.facilitiesAndServices_ar[index]],
    };

    this.updateFeatures(updateData);
  }

  updateFeatures(data: any): void {
    if (!this.selectedUnit) return;

    this.featuresLoading = true;

    this.api
      .patch<{ unit: Unit }>(`/broker/unit/${this.selectedUnit._id}/features`, data)
      .pipe(finalize(() => (this.featuresLoading = false)))
      .subscribe({
        next: (res) => {
          this.selectedUnit = res.unit;
          this.load();
          this.newFeatureEn = '';
          this.newFeatureAr = '';
          alert('Features updated successfully!');
        },
        error: (err) => alert(err?.message || 'Failed to update features.'),
      });
  }

  // ── Price Management ─────────────────────────────────────────────────────────

  openPriceModal(unit: Unit): void {
    this.selectedUnit = unit;
    this.initializePriceForm(unit);
    this.showPriceModal = true;
  }

  closePriceModal(): void {
    this.showPriceModal = false;
    this.selectedUnit = null;
  }

  initializePriceForm(unit: Unit): void {
    this.priceForm = {
      rentPrice: unit.rentPrice
        ? {
          rentType: unit.rentPrice.rentType,
          price: unit.rentPrice.price,
          currency: unit.rentPrice.currency,
          commission: unit.rentPrice.commission,
          securityDeposit: unit.rentPrice.securityDeposit,
          tiers: [...unit.rentPrice.tiers],
        }
        : {
          rentType: 'daily',
          price: 0,
          currency: 'EGP',
          commission: { type: 'percentage', value: 5 },
          securityDeposit: 0,
          tiers: [],
        },
      salePrice: unit.salePrice
        ? {
          basePrice: unit.salePrice.basePrice,
          pricePerMeter: unit.salePrice.pricePerMeter,
          currency: unit.salePrice.currency,
          negotiable: unit.salePrice.negotiable,
          commission: unit.salePrice.commission,
          plans: [...unit.salePrice.plans],
        }
        : {
          basePrice: 0,
          pricePerMeter: 0,
          currency: 'EGP',
          negotiable: false,
          commission: { type: 'percentage', value: 5 },
          plans: [],
        },
    };
  }

  addPriceTier(): void {
    this.priceForm.rentPrice.tiers.push({
      minDays: 1,
      pricePerDay: this.priceForm.rentPrice.price,
      label: '',
    });
  }

  removePriceTier(index: number): void {
    this.priceForm.rentPrice.tiers.splice(index, 1);
  }

  addSalePlan(): void {
    this.priceForm.salePrice.plans.push({
      name_en: '',
      name_ar: '',
      saleType: 'cash',
      downPaymentPercent: 100,
      downPaymentAmount: this.priceForm.salePrice.basePrice,
      years: 0,
      interestRate: 0,
      discountPercent: 0,
    });
  }

  removeSalePlan(index: number): void {
    this.priceForm.salePrice.plans.splice(index, 1);
  }

  updatePrice(): void {
    if (!this.selectedUnit) return;

    this.priceLoading = true;

    const priceData = {
      rentPrice: this.selectedUnit.purpose === 'sale' ? undefined : this.priceForm.rentPrice,
      salePrice: this.selectedUnit.purpose === 'rent' ? undefined : this.priceForm.salePrice,
    };

    this.api
      .patch<{ unit: Unit }>(`/broker/unit/${this.selectedUnit._id}/price`, priceData)
      .pipe(finalize(() => (this.priceLoading = false)))
      .subscribe({
        next: (res) => {
          this.closePriceModal();
          this.load();
          alert('Price updated successfully!');
        },
        error: (err) => alert(err?.message || 'Failed to update price.'),
      });
  }

  // ── Helper Methods ───────────────────────────────────────────────────────────

  purposeBadge(purpose: string): string {
    const map: Record<string, string> = {
      rent: 'bg-blue-100 text-blue-700',
      sale: 'bg-green-100 text-green-700',
      sale_and_rent: 'bg-purple-100 text-purple-700',
    };
    return map[purpose] ?? 'bg-gray-100 text-gray-600';
  }

  statusBadge(status: string): string {
    const map: Record<string, string> = {
      active: 'bg-green-100 text-green-700',
      sold: 'bg-gray-100 text-gray-600',
      rented: 'bg-blue-100 text-blue-700',
      inactive: 'bg-red-100 text-red-600',
    };
    return map[status] ?? 'bg-gray-100 text-gray-600';
  }

  thumbnail(unit: Unit): string {
    return unit.gallery?.[0]?.secure_url ?? '';
  }

  formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString();
  }

  // ── Panels ───────────────────────────────────────────────────────────────────

  openPricing(unit: Unit): void {
    this.pricingUnitId = unit._id;
  }

  closePricing(): void {
    this.pricingUnitId = null;
  }

  openAvailabilityPanel(unit: Unit): void {
    this.availabilityUnitId = unit._id;
  }

  closeAvailabilityPanel(): void {
    this.availabilityUnitId = null;
  }
}
